import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000;

const TYPE_DOT = {
  restroom_submitted: "#0ea5e9",
  restroom_approved: "#16a34a",
  restroom_rejected: "#ef4444",
  review_posted: "#a855f7",
  review_removed: "#dc2626",
  sponsorship_received: "#ec4899",
  sponsorship_activated: "#22c55e",
  incinerator_vote: "#f97316",
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const sec = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
};

export default function NotificationBell({ token, isAuthenticated }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const popRef = useRef(null);

  const load = async () => {
    if (!isAuthenticated || !token) return;
    try {
      const r = await axios.get(`${API}/notifications?limit=15`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(r.data.items || []);
      setUnread(r.data.total_unread || 0);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const markRead = async (id) => {
    try {
      await axios.post(
        `${API}/notifications/${id}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems((prev) => prev.map((n) => (n.notification_id === id ? { ...n, is_read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (_) {}
  };

  const markAll = async () => {
    try {
      await axios.post(
        `${API}/notifications/read_all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
    } catch (_) {}
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative" ref={popRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed top-20 right-36 z-20 w-12 h-12 bg-white border border-slate-200 rounded-full shadow-md flex items-center justify-center text-slate-700 hover:bg-slate-50"
        data-testid="notification-bell"
        title="Notifications"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-5 h-5 px-1 flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-36 right-4 z-30 w-80 max-w-[92vw] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
            data-testid="notification-popover"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <h3 className="font-bold">Notifications</h3>
              {unread > 0 && (
                <button
                  onClick={markAll}
                  className="text-xs text-sky-600 font-semibold hover:underline"
                  data-testid="notif-mark-all-read"
                >
                  Mark all read
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-10">No notifications yet</p>
              ) : (
                items.map((n) => {
                  const dotColor = TYPE_DOT[n.type] || "#64748b";
                  return (
                    <button
                      key={n.notification_id}
                      onClick={() => {
                        if (!n.is_read) markRead(n.notification_id);
                        if (n.link) {
                          setOpen(false);
                          navigate(n.link);
                        }
                      }}
                      className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 flex gap-3 transition ${
                        !n.is_read ? "bg-sky-50/40" : ""
                      }`}
                      data-testid={`notif-item-${n.notification_id}`}
                    >
                      <span
                        className="mt-1.5 inline-block w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: dotColor }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!n.is_read ? "font-semibold text-slate-900" : "text-slate-700"}`}>
                          {n.title}
                        </p>
                        {n.body && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                      {!n.is_read && <Check size={14} className="text-sky-500 mt-1.5" />}
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
